/**
 * Página de Destinos — vista moderna.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Switch, Tooltip, Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DataTable from '../common/DataTable';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const emptyForm = { nombre: '', descripcion: '', ubicacion: '', tiempo_trayecto_minutos: '' };

export default function DestinosPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();

  const [destinos, setDestinos] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActivo, setFilterActivo] = useState('');

  const [slideOpen, setSlideOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, pRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/destinos?empresa_id=${empresaId}&solo_activos=false`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}&solo_activos=false`, { headers: getAuthHeaders() }),
      ]);
      setDestinos(await dRes.json());
      setPaquetes(await pRes.json());
    } catch (e) { toast.error('Error cargando destinos'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return destinos.filter(d => {
      if (q && !(d.nombre || '').toLowerCase().includes(q) && !(d.ubicacion || '').toLowerCase().includes(q)) return false;
      if (filterActivo === 'activos' && !d.activo) return false;
      if (filterActivo === 'inactivos' && d.activo) return false;
      return true;
    });
  }, [destinos, search, filterActivo]);

  const countPaquetes = useCallback((destinoId) =>
    paquetes.filter(p => p.destino_id === destinoId).length, [paquetes]);

  const handleNew = () => { setEditItem(null); setForm(emptyForm); setSlideOpen(true); };

  const handleEdit = (row) => {
    setEditItem(row);
    setForm({
      nombre: row.nombre, descripcion: row.descripcion || '',
      ubicacion: row.ubicacion || '',
      tiempo_trayecto_minutos: row.tiempo_trayecto_minutos != null ? String(row.tiempo_trayecto_minutos) : '',
    });
    setSlideOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('Falta el nombre');
    setSaving(true);
    try {
      const body = {
        nombre: form.nombre.trim(), descripcion: form.descripcion,
        ubicacion: form.ubicacion,
        tiempo_trayecto_minutos: form.tiempo_trayecto_minutos ? parseInt(form.tiempo_trayecto_minutos) : null,
      };
      if (!editItem) body.empresa_id = empresaId;
      const url = editItem ? `${API_BASE}/turismo/destinos/${editItem.id}` : `${API_BASE}/turismo/destinos`;
      const res = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success(editItem ? 'Destino actualizado' : 'Destino creado');
      setSlideOpen(false);
      cargar();
      refreshCounts?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleToggleActivo = async (row, nuevoEstado) => {
    setDestinos(ds => ds.map(d => d.id === row.id ? { ...d, activo: nuevoEstado } : d));
    try {
      const res = await fetch(`${API_BASE}/turismo/destinos/${row.id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo: nuevoEstado }),
      });
      if (!res.ok) throw new Error();
      toast.success(nuevoEstado ? 'Destino activado' : 'Destino desactivado');
      refreshCounts?.();
    } catch {
      setDestinos(ds => ds.map(d => d.id === row.id ? { ...d, activo: !nuevoEstado } : d));
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE}/turismo/destinos/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('No se pudo eliminar (puede tener paquetes asociados)');
      toast.success('Destino eliminado');
      setDeleteTarget(null);
      cargar();
      refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  const handleBulkDelete = async () => {
    if (!deletingBulk) return;
    try {
      await Promise.all(deletingBulk.ids.map(id =>
        fetch(`${API_BASE}/turismo/destinos/${id}`, { method: 'DELETE', headers: getAuthHeaders() })
      ));
      toast.success(`${deletingBulk.ids.length} destino(s) eliminado(s)`);
      deletingBulk.run(); setDeletingBulk(null); cargar(); refreshCounts?.();
    } catch { toast.error('Error en eliminación masiva'); }
  };

  const handleBulkToggle = async (ids, activo, clear) => {
    try {
      await Promise.all(ids.map(id =>
        fetch(`${API_BASE}/turismo/destinos/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo }) })
      ));
      toast.success(`${ids.length} actualizado(s)`);
      clear(); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const columns = [
    { key: 'nombre', label: 'Destino', sortable: true,
      render: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#0ea5e915', color: '#0ea5e9', width: 32, height: 32 }}>
            <BeachAccessIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }} noWrap>{r.nombre}</Typography>
            {r.ubicacion && <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>📍 {r.ubicacion}</Typography>}
          </Box>
        </Box>
      ) },
    { key: 'descripcion', label: 'Descripción',
      render: r => <Typography sx={{ fontSize: 12, color: '#475569' }} noWrap title={r.descripcion}>{r.descripcion || '—'}</Typography> },
    { key: 'tiempo_trayecto_minutos', label: 'Trayecto', sortable: true, align: 'center',
      render: r => r.tiempo_trayecto_minutos ? <Chip size="small" label={`${r.tiempo_trayecto_minutos} min`} sx={{ height: 20, fontSize: 11 }} /> : '—' },
    { key: 'paquetes_count', label: '# Paquetes', sortable: true, align: 'center',
      sortValue: r => countPaquetes(r.id),
      render: r => {
        const c = countPaquetes(r.id);
        return c > 0
          ? <Chip size="small" label={c} sx={{ height: 20, fontSize: 11, bgcolor: '#0d948815', color: '#0d9488', fontWeight: 700 }} />
          : <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>0</Typography>;
      } },
    { key: 'activo', label: 'Activo', align: 'center', sortable: true,
      render: r => <Switch size="small" checked={!!r.activo} onClick={e => e.stopPropagation()} onChange={(_, v) => handleToggleActivo(r, v)} /> },
    { key: 'acciones', label: '', align: 'right',
      render: r => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(r)} sx={{ color: '#0d9488' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setDeleteTarget(r)} sx={{ color: '#dc2626' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Box>
      ) },
  ];

  const bulkActions = [
    { label: 'Activar', icon: <VisibilityIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, true, c) },
    { label: 'Desactivar', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, false, c) },
    { label: 'Eliminar', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => setDeletingBulk({ ids, run: c }) },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Destinos</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
          Lugares geográficos a los que llevas turistas. Cada paquete pertenece a un destino.
        </Typography>
      </Box>

      <DataTable
        columns={columns} rows={filteredRows} loading={loading} rowKey="id" onRowClick={handleEdit}
        search={{ value: search, onChange: setSearch, placeholder: 'Buscar destino...' }}
        filters={[
          { key: 'estado', label: 'Estado', value: filterActivo, onChange: setFilterActivo,
            options: [{ value: 'activos', label: 'Solo activos' }, { value: 'inactivos', label: 'Solo inactivos' }] },
        ]}
        bulkActions={bulkActions}
        actions={
          <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleNew}
            sx={{ textTransform: 'none', fontWeight: 600,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)' }}>
            Nuevo destino
          </Button>
        }
        emptyState={destinos.length === 0
          ? <EmptyState icon={<BeachAccessIcon />} title="Aún no hay destinos" description="Crea tu primer destino turístico." ctaLabel="Crear destino" onCta={handleNew} />
          : <EmptyState icon={<BeachAccessIcon />} title="Sin resultados" size="sm" />}
      />

      <SlideOver open={slideOpen} onClose={() => setSlideOpen(false)}
        title={editItem ? 'Editar Destino' : 'Nuevo Destino'} icon={<BeachAccessIcon />}
        subtitle={editItem ? editItem.nombre : 'Datos del destino turístico'}
        footer={<>
          <Button onClick={() => setSlideOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disableElevation disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)' }}>
            {saving ? 'Guardando...' : (editItem ? 'Guardar' : 'Crear')}
          </Button>
        </>}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Nombre *" size="small" fullWidth value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
          <TextField label="Ubicación" size="small" fullWidth value={form.ubicacion} onChange={e => setForm({ ...form, ubicacion: e.target.value })} placeholder="Ej: Cartagena, Bolívar" />
          <TextField label="Descripción" size="small" fullWidth multiline rows={3} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} />
          <TextField label="Tiempo de trayecto (minutos)" size="small" type="number" fullWidth value={form.tiempo_trayecto_minutos}
            onChange={e => setForm({ ...form, tiempo_trayecto_minutos: e.target.value })} />
        </Box>
      </SlideOver>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="¿Eliminar este destino?"
        message={deleteTarget ? `Vas a eliminar "${deleteTarget.nombre}". Si tiene paquetes asociados, no se podrá eliminar.` : ''}
        confirmText="Eliminar" />

      <ConfirmDialog open={!!deletingBulk} onClose={() => setDeletingBulk(null)} onConfirm={handleBulkDelete}
        title={`¿Eliminar ${deletingBulk?.ids?.length || 0} destino(s)?`}
        message="Esta acción no se puede deshacer."
        confirmText="Eliminar todos" />

      <ToastContainer />
    </Box>
  );
}
